import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const CreateGroupModal = ({ open, onClose }) => {
  const { user } = useContext(AuthContext);
  const [groupName, setGroupName] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading, setLoading] = useState(false);

  // autocomplete state
  const [contactOptions, setContactOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    if (!open) return;
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp", {
          params: { companyId: user.companyId },
        });
        setWhatsapps(data);
        if (data.length > 0) setWhatsappId(data[0].id);
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
    setGroupName("");
    setSelectedContacts([]);
    setSearchInput("");
    setContactOptions([]);
  }, [open, user.companyId]);

  const searchContacts = useCallback(async (value) => {
    setSearchLoading(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: value, pageNumber: 1 },
      });
      setContactOptions(data.contacts || []);
    } catch (err) {
      toastError(err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchContacts(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchContacts]);

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.warning("Nome do grupo é obrigatório");
      return;
    }
    if (selectedContacts.length < 2) {
      toast.warning("Selecione pelo menos 2 contatos");
      return;
    }
    if (!whatsappId) {
      toast.warning("Selecione um WhatsApp");
      return;
    }

    try {
      setLoading(true);
      const participants = selectedContacts.map((c) => c.number);
      const response = await api.post("/groups/create", {
        groupName,
        participants,
        whatsappId: parseInt(whatsappId),
      });

      if (response.data.success) {
        toast.success("Grupo criado com sucesso!");
        onClose();
      } else {
        toast.error(response.data.error || "Erro ao criar grupo");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Criar Novo Grupo</DialogTitle>
      <DialogContent style={{ paddingTop: "20px" }}>
        <TextField
          autoFocus
          margin="dense"
          label="Nome do Grupo"
          type="text"
          fullWidth
          variant="outlined"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          disabled={loading}
          style={{ marginBottom: "16px" }}
        />

        <FormControl fullWidth style={{ marginBottom: "16px" }}>
          <InputLabel>WhatsApp</InputLabel>
          <Select
            value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
            disabled={loading}
          >
            {whatsapps.map((wa) => (
              <MenuItem key={wa.id} value={wa.id}>
                {wa.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          options={contactOptions.filter(
            (opt) => !selectedContacts.find((s) => s.id === opt.id)
          )}
          getOptionLabel={(option) =>
            `${option.name} (${option.number})`
          }
          filterOptions={(x) => x}
          value={selectedContacts}
          onChange={(_, newValue) => setSelectedContacts(newValue)}
          inputValue={searchInput}
          onInputChange={(_, value, reason) => {
            if (reason !== "reset") setSearchInput(value);
          }}
          loading={searchLoading}
          disabled={loading}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Pesquisar Contatos"
              placeholder="Digite para buscar..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="Nenhum contato encontrado"
          loadingText="Buscando..."
        />

        {selectedContacts.length > 0 && (
          <Box style={{ marginTop: "8px" }}>
            <small style={{ color: "#666" }}>
              {selectedContacts.length} contato(s) selecionado(s)
            </small>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreateGroup}
          color="primary"
          variant="contained"
          disabled={loading || selectedContacts.length < 2 || !groupName.trim()}
        >
          {loading ? <CircularProgress size={24} /> : "Criar Grupo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupModal;
